// src/pages/DepotSebaranMap.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

import 'leaflet.markercluster/dist/MarkerCluster.css'; // ✅ Import MarkerCluster CSS
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'; // ✅ Import MarkerCluster default styling
import 'leaflet.markercluster'; // ✅ Import MarkerCluster JS (akan melampirkan ke L)

import '../styles/DepotSebaranMap.css';

// FIX: Leaflet default icon issues - Pastikan baris ini di sini, setelah SEMUA import
delete L.Icon.Default.prototype._get;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});


const DepotSebaranMap = () => {
    const mapRef = useRef(null);
    const leafletMapInstanceRef = useRef(null);
    // ✅ Ganti FeatureGroup dengan MarkerClusterGroup
    const markerClusterGroupRef = useRef(null); // Ref untuk MarkerClusterGroup

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [depotData, setDepotData] = useState([]);

    // --- Fetch Data Depot Publik ---
    useEffect(() => {
        const fetchPublicDepotData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get('https://depotaircerdas.com/api/public/depot-public');
                setDepotData(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching public depot data:', err);
                setError('Gagal memuat data depot. ' + (err.response?.data?.message || err.message || 'Silakan cek koneksi atau server backend.'));
                setLoading(false);
            }
        };

        fetchPublicDepotData();
    }, []);

    // --- Inisialisasi Peta (Hanya Sekali) ---
    useEffect(() => {
        if (mapRef.current && !leafletMapInstanceRef.current) {
            leafletMapInstanceRef.current = L.map(mapRef.current).setView([-2.5, 118.0], 5);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(leafletMapInstanceRef.current);

            // ✅ Inisialisasi MarkerClusterGroup dan tambahkan ke peta
            markerClusterGroupRef.current = L.markerClusterGroup();
            leafletMapInstanceRef.current.addLayer(markerClusterGroupRef.current);
        }

        // Cleanup function for Leaflet map instance when component unmounts
        return () => {
            if (leafletMapInstanceRef.current) {
                leafletMapInstanceRef.current.remove();
                leafletMapInstanceRef.current = null;
                markerClusterGroupRef.current = null; // Reset cluster group ref as well
            }
        };
    }, []);

    // --- Render Marker saat depotData Berubah ---
    useEffect(() => {
        if (!leafletMapInstanceRef.current || !depotData.length || !markerClusterGroupRef.current) return;

        // ✅ Hapus semua marker dari cluster group
        markerClusterGroupRef.current.clearLayers();

        let bounds = [];
        depotData.forEach(d => {
            const lat = parseFloat(d.latitude);
            const lng = parseFloat(d.longitude);

            if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Invalid coordinates for depot ${d.nama}: Lat ${d.latitude}, Lng ${d.longitude}`);
                return;
            }

            let color = "orange";
            if (d.is_subscribed === "Ya") color = "green";
            else if (d.is_subscribed === "Tidak") color = "red";

            const popupContent = `
                <div style='font-size:14px; max-width:250px;'>
                    <b style='font-size:16px;'>${d.nama?.toUpperCase()}</b><br>
                    <i>${d.alamat}</i><br><br>
                    <b>Telepon:</b> ${d.telepon || '-'}<br>
                    <b>Jam Buka:</b> ${d.jam_buka || '-'}<br>
                    <b>Kelurahan:</b> ${d.kelurahan || '-'}<br>
                    <b>Kecamatan:</b> ${d.kecamatan || '-'}<br>
                    <b>Kota/Kab:</b> ${d.kota_kabupaten || '-'}<br>
                </div>
            `;
            
            // ✅ Gunakan L.marker (default) atau L.circleMarker
            // L.marker lebih sering digunakan dengan cluster group
            const marker = L.marker([lat, lng], { // Menggunakan L.marker
                icon: L.divIcon({ // Membuat ikon div kustom untuk warna
                    className: 'custom-div-icon',
                    html: `<div style="background-color: ${color}; border-radius: 50%; width: 16px; height: 16px; border: 2px solid #fff; box-shadow: 0 0 5px rgba(0,0,0,0.5);"></div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                })
            }).bindPopup(popupContent);
            // Atau jika ingin tetap L.circleMarker (tapi L.marker lebih umum untuk clustering):
            /*
            const marker = L.circleMarker([lat, lng], {
                radius: 8, fillColor: color, color: "#000", weight: 1, opacity: 1, fillOpacity: 0.8
            }).bindPopup(popupContent);
            */

            markerClusterGroupRef.current.addLayer(marker); // ✅ Tambahkan marker ke cluster group

            bounds.push([lat, lng]);
        });

        if (bounds.length > 0 && leafletMapInstanceRef.current) {
            leafletMapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [depotData]);

    return (
        <div>
            <Helmet>
                <title>Peta Sebaran Depot Air Minum Indonesia | Temukan Lokasi Depot Terdekat</title>
                <meta name="description" content="Jelajahi peta interaktif sebaran depot air minum Depot Air Cerdas di seluruh Indonesia. Temukan lokasi depot terdekat, alamat, dan status langganan (Sudah Langganan, Belum Langganan, Under Prospect). Membantu Anda menemukan penyedia air minum isi ulang terpercaya." />
                <meta name="keywords" content="peta depot air minum, sebaran depot air, lokasi depot air, depot air terdekat, peta air galon, depot air isi ulang, Depot Air Cerdas, peta Indonesia, depot air Depok, depot air Jakarta, depot air Jawa Barat" />
                <link rel="canonical" href="https://depotaircerdas.com/sebaran-depot" />
                
                <meta property="og:title" content="Peta Sebaran Depot Air Minum Indonesia | Depot Air Cerdas" />
                <meta property="og:description" content="Jelajahi peta interaktif sebaran depot air minum Depot Air Cerdas di seluruh Indonesia. Temukan lokasi depot terdekat, alamat, dan status langganan (Sudah Langganan, Belum Langganan, Under Prospect)." />
                <meta property="og:url" content="https://depotaircerdas.com/sebaran-depot" />
                <meta property="og:image" content="https://depotaircerdas.com/images/peta-sebaran-depot-air-cerdas.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="id_ID" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Peta Sebaran Depot Air Minum Indonesia | Depot Air Cerdas" />
                <meta name="twitter:description" content="Jelajahi peta interaktif sebaran depot air minum Depot Air Cerdas di seluruh Indonesia. Temukan lokasi depot terdekat, alamat, dan status langganan (Sudah Langganan, Belum Langganan, Under Prospect)." />
                <meta name="twitter:image" content="https://depotaircerdas.com/images/peta-sebaran-depot-air-cerdas.jpg" />
            </Helmet>

            <Navbar />
            <div className="sebaran-map-container">
                <h1 className="sebaran-map-title">Peta Sebaran Depot Air Minum di Indonesia</h1>
                <p className="sebaran-map-intro">
                    Jelajahi lokasi depot air minum kami di seluruh Indonesia. Peta ini menampilkan titik-titik depot berdasarkan status langganan mereka. Ini adalah alat bantu visual untuk menemukan depot terdekat dan memahami distribusi layanan kami.
                </p>

                {loading && <div className="map-loading-overlay">Memuat peta sebaran depot...</div>}
                {error && <p className="error-message">{error}</p>}

                <div id="map-sebaran" ref={mapRef} style={{ height: '600px', width: '100%', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}></div>

                <div className="sebaran-legend">
                    <b>🟢 Legenda Status Depot:</b><br />
                    <div><span className="dot green-dot"></span> Sudah Langganan</div>
                    <div><span className="dot red-dot"></span> Belum Langganan</div>
                    <div><span className="dot orange-dot"></span> Under Prospect</div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default DepotSebaranMap;